import { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import InitApi from "api/InitApi";
import useTokenStore from "store/tokenStore";
import Dialog from "components/Overlays/ResponsiveDialog";

export const tokenAbilities = [
    { value: "subscriptions:allocate", label: "Allocate subscriptions" },
    { value: "subscriptions:activate", label: "Activate subscriptions" },
    { value: "subscriptions:deactivate", label: "Deactivate subscriptions" },
];

export default function TokenDialog({ onClose, onGenerated, services = [] }) {
    const { accessToken } = useTokenStore();
    const client = useQueryClient();
    const pending = useRef(false);
    const [form, setForm] = useState({ service_name: "", name: "", abilities: tokenAbilities.map(({ value }) => value), expires_at: "" });
    const [saving, setSaving] = useState(false);
    const [issued, setIssued] = useState(null);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const formRef = useRef(null);
    const save = async (event) => {
        event.preventDefault();
        if (pending.current) return;
        if (!form.service_name.trim() || !form.name.trim() || !form.abilities.length) {
            setError("Enter a service name, token name, and at least one permission.");
            formRef.current?.querySelector(!form.service_name.trim() ? "#service-name" : !form.name.trim() ? "#token-name" : 'input[type="checkbox"]')?.focus();
            return;
        }
        pending.current = true; setSaving(true); setError("");
        try {
            const api = await InitApi({ accessToken });
            const response = await api.post("/admin/service-tokens", { ...form, service_name: form.service_name.trim(), name: form.name.trim(), expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null });
            setIssued(response.data.data);
            onGenerated();
            await client.invalidateQueries("service-tokens");
        } catch (failure) {
            const data = failure.response?.data;
            setError(data?.errors ? Object.values(data.errors).flat().join(" ") : data?.message || "Could not generate a token. Please try again.");
        } finally { pending.current = false; setSaving(false); }
    };
    const copy = async () => {
        try { await navigator.clipboard.writeText(issued.plain_text_token); setCopied(true); setError(""); }
        catch { setError("Select and copy the token below."); }
    };
    return <Dialog visible header={issued ? "Copy your service token" : "Generate service token"} onHide={() => { if (!saving) onClose(); }} closable={!saving} closeOnEscape={!saving} style={{ width: "560px" }}>
        {issued ? <div className="service-token-form">
            <p>This token belongs to <strong>{issued.service_name}</strong>. Copy it now; you won’t be able to view it again.</p>
            <label htmlFor="generated-service-token">Service token</label>
            <textarea id="generated-service-token" value={issued.plain_text_token} readOnly autoFocus spellCheck={false} onFocus={(event) => event.target.select()} />
            <p>Store it in your service’s secret store and send the token in the <code>X-Service-Token</code> header. Use the token value directly, without a Bearer prefix.</p>
            {error && <p role="alert" className="service-token-error">{error}</p>}
            {copied && <p role="status">Token copied.</p>}
            <div className="service-token-actions"><button type="button" onClick={copy}>Copy token</button><button type="button" className="is-primary" onClick={onClose}>Done</button></div>
        </div> : <form ref={formRef} className="service-token-form" onSubmit={save}>
            <p>Use the same service name when issuing a replacement token to keep access to that service’s existing allocations.</p>
            <fieldset disabled={saving}>
                <label htmlFor="service-name">Service name</label>
                <input id="service-name" list="external-service-names" maxLength={200} autoFocus value={form.service_name} onChange={(event) => setForm({ ...form, service_name: event.target.value })} autoComplete="off" />
                <datalist id="external-service-names">{services.map((service) => <option key={service.id} value={service.name} />)}</datalist>
                <label htmlFor="token-name">Token name</label>
                <input id="token-name" maxLength={200} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="For example, production" autoComplete="off" />
                <fieldset className="service-token-permissions"><legend>Permissions</legend>{tokenAbilities.map(({ value, label }) => <label key={value}><input type="checkbox" checked={form.abilities.includes(value)} onChange={(event) => setForm({ ...form, abilities: event.target.checked ? [...form.abilities, value] : form.abilities.filter((ability) => ability !== value) })} />{label}</label>)}</fieldset>
                <label htmlFor="token-expiry">Expiry date and time <span>(optional)</span></label>
                <input type="datetime-local" id="token-expiry" value={form.expires_at} onChange={(event) => setForm({ ...form, expires_at: event.target.value })} />
                <p className="service-token-muted">Uses your local timezone. Leave blank for no scheduled expiry.</p>
            </fieldset>
            {error && <p role="alert" className="service-token-error">{error}</p>}
            <div className="service-token-actions"><button type="button" disabled={saving} onClick={onClose}>Cancel</button><button type="submit" className="is-primary" disabled={saving}>{saving ? "Generating…" : "Generate token"}</button></div>
        </form>}
    </Dialog>;
}
